import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BACKGROUND_REFETCH_MS, queryKeys } from '@/api/queryKeys'
import * as journalApi from '@/api/journal'
import type { JournalEntry, JournalEntryType, JournalLink, JournalBucketLink } from '@/types'

export function useJournalEntries(
  search?: string,
  typeFilter?: JournalEntry['type'] | null,
): JournalEntry[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.entries(search, typeFilter),
    queryFn: () => journalApi.fetchJournalEntries(search, typeFilter),
    refetchOnWindowFocus: true,
    refetchInterval: BACKGROUND_REFETCH_MS,
  })
  return data
}

export function useJournalEntry(id: string | null): JournalEntry | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.entry(id),
    queryFn: () => journalApi.fetchJournalEntry(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  })
  return data ?? undefined
}

export function useJournalLinks(journalId: string | null): JournalLink[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.links(journalId),
    queryFn: () => journalApi.fetchJournalLinks(journalId!),
    enabled: !!journalId,
  })
  return journalId ? data : []
}

/** Returns a map of journalId → count of linked priorities only. */
export function useJournalLinkCounts(): Map<string, number> | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.linkCounts(),
    queryFn: journalApi.fetchJournalLinkCounts,
  })
  return data ? new Map(Object.entries(data)) : undefined
}

export function useJournalBucketLinks(journalId: string | null): JournalBucketLink[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.bucketLinks(journalId),
    queryFn: () => journalApi.fetchJournalBucketLinks(journalId!),
    enabled: !!journalId,
  })
  return journalId ? data : []
}

/** Returns a map of journalId → total link count (priorities + buckets). */
export function useJournalLinkCountsAll(): Map<string, number> | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.linkCounts(),
    queryFn: journalApi.fetchJournalLinkCounts,
  })
  return data ? new Map(Object.entries(data)) : undefined
}

export function useEntriesForPriority(priorityId: string | null): JournalEntry[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.forPriority(priorityId),
    queryFn: () => journalApi.fetchEntriesForPriority(priorityId!),
    enabled: !!priorityId,
  })
  return priorityId ? data : []
}

export function useEntriesForBucket(bucketId: string | null): JournalEntry[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.journal.forBucket(bucketId),
    queryFn: () => journalApi.fetchEntriesForBucket(bucketId!),
    enabled: !!bucketId,
  })
  return bucketId ? data : []
}

function invalidateJournal(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['journal'] })
}

export function useCreateJournalEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ type, body }: { type?: JournalEntryType; body?: string }) => journalApi.createEntry(type, body),
    onSuccess: () => invalidateJournal(qc),
  })
}

export function useUpdateJournalEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof journalApi.updateEntry>[1] }) =>
      journalApi.updateEntry(id, data),
    onSuccess: () => invalidateJournal(qc),
  })
}

export function useDeleteJournalEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: journalApi.deleteEntry,
    onSuccess: () => invalidateJournal(qc),
  })
}

export function useAddJournalLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ journalId, priorityId }: { journalId: string; priorityId: string }) =>
      journalApi.addJournalLink(journalId, priorityId),
    onSuccess: () => invalidateJournal(qc),
  })
}

export function useRemoveJournalLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ journalId, priorityId }: { journalId: string; priorityId: string }) =>
      journalApi.removeJournalLink(journalId, priorityId),
    onSuccess: () => invalidateJournal(qc),
  })
}

export function useAddJournalBucketLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ journalId, bucketId }: { journalId: string; bucketId: string }) =>
      journalApi.addJournalBucketLink(journalId, bucketId),
    onSuccess: () => invalidateJournal(qc),
  })
}

export function useRemoveJournalBucketLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ journalId, bucketId }: { journalId: string; bucketId: string }) =>
      journalApi.removeJournalBucketLink(journalId, bucketId),
    onSuccess: () => invalidateJournal(qc),
  })
}
